import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trash2, Check, Plus, LogOut, ArrowLeft, Loader2, Shield, Flag, Users, Star, Ban, VolumeX, Volume2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StarRating, StarInput } from "@/components/StarRating";
import { Avatar } from "@/components/Avatar";
import { DigitalBackground } from "@/components/DigitalBackground";
import { toast } from "sonner";

const TABS = [
  { key: "reviews", label: "Reviews", icon: Star },
  { key: "reports", label: "Reports", icon: Flag },
  { key: "users", label: "Users", icon: Users },
];

export default function Admin() {
  const { user, loading, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("reviews");

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) navigate("/");
  }, [loading, user, isAdmin, navigate]);

  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-black text-white font-body">
      <DigitalBackground />
      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-400 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to site
            </button>
            <h1 className="mt-3 font-heading font-black text-3xl sm:text-4xl flex items-center gap-3">
              <Shield className="w-7 h-7 text-cyan-400" />
              <span className="text-gradient-accent">Admin Panel</span>
            </h1>
            <p className="mt-2 text-sm text-slate-400">Signed in as {user.name} · {user.email}</p>
          </div>
          <button
            data-testid="admin-logout"
            onClick={() => { logout(); navigate("/"); }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-white hover:border-red-400/50 hover:text-red-400 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>

        <div className="mt-8 flex flex-wrap gap-2 border-b border-white/10 pb-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 rounded-t-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                  tab === t.key ? "text-cyan-400 border-b-2 border-cyan-400" : "text-slate-400 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-8">
          {tab === "reviews" && <ReviewsAdmin />}
          {tab === "reports" && <ReportsAdmin />}
          {tab === "users" && <UsersAdmin adminId={user.id} />}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Reviews
function ReviewsAdmin() {
  const [reviews, setReviews] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {}).finally(() => setFetching(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => { await api.patch(`/admin/reviews/${id}`, { status }); load(); };
  const remove = async (id) => { await api.delete(`/admin/reviews/${id}`); load(); };
  const add = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/admin/reviews", { name, rating, comment, status: "approved" });
      setName(""); setComment(""); setRating(5); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pending = reviews.filter((r) => r.status === "pending");
  const approved = reviews.filter((r) => r.status === "approved");

  const Card = ({ r }) => (
    <div data-testid={`admin-review-${r.id}`} className="electric-card rounded-xl bg-black/50 backdrop-blur-xl border border-white/10 p-5">
      <div className="flex items-center justify-between">
        <p className="font-heading font-bold text-white text-sm">{r.name}</p>
        <StarRating value={r.rating} />
      </div>
      <p className="mt-2 text-sm text-slate-300">{r.comment}</p>
      <div className="mt-4 flex items-center gap-2">
        {r.status === "pending" ? (
          <button data-testid={`admin-approve-${r.id}`} onClick={() => setStatus(r.id, "approved")} className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 text-xs font-semibold px-3 py-2 hover:bg-cyan-500/25"><Check className="w-3.5 h-3.5" /> Add to website</button>
        ) : (
          <button data-testid={`admin-unapprove-${r.id}`} onClick={() => setStatus(r.id, "pending")} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 border border-white/15 text-slate-300 text-xs font-semibold px-3 py-2 hover:bg-white/10">Hide from website</button>
        )}
        <button data-testid={`admin-delete-${r.id}`} onClick={() => remove(r.id)} className="inline-flex items-center gap-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-semibold px-3 py-2 hover:bg-red-500/20"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
      </div>
    </div>
  );

  return (
    <div>
      <div className="electric-card rounded-2xl bg-black/50 backdrop-blur-xl border border-white/10 p-6">
        <p className="font-heading font-bold text-white text-lg flex items-center gap-2"><Plus className="w-5 h-5 text-cyan-400" /> Add a review to the website</p>
        <form onSubmit={add} className="mt-4 grid sm:grid-cols-2 gap-4">
          <div className="space-y-3">
            <StarInput value={rating} onChange={setRating} />
            <input data-testid="admin-add-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer name" required className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none" />
          </div>
          <textarea data-testid="admin-add-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Review comment" required rows={3} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none resize-none" />
          <button type="submit" disabled={busy} data-testid="admin-add-submit" className="btn-electric text-black font-bold text-sm px-6 py-3 rounded-lg sm:col-span-2 disabled:opacity-60">{busy ? "Adding…" : "Add & Publish Review"}</button>
        </form>
      </div>

      {fetching ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-cyan-400 animate-spin" /></div>
      ) : (
        <>
          <h2 className="mt-12 font-heading font-bold text-xl text-white">Pending approval <span className="text-cyan-400">({pending.length})</span></h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid="admin-pending-list">
            {pending.length === 0 ? <p className="text-sm text-slate-500">No pending reviews.</p> : pending.map((r) => <Card key={r.id} r={r} />)}
          </div>
          <h2 className="mt-12 font-heading font-bold text-xl text-white">Live on website <span className="text-cyan-400">({approved.length})</span></h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid="admin-approved-list">
            {approved.length === 0 ? <p className="text-sm text-slate-500">No approved reviews yet.</p> : approved.map((r) => <Card key={r.id} r={r} />)}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Reports
function ReportsAdmin() {
  const [reports, setReports] = useState([]);
  const [fetching, setFetching] = useState(true);
  const load = useCallback(() => { api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => {}).finally(() => setFetching(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const ban = async (id) => { await api.post(`/admin/users/${id}/ban`); toast.success("User banned"); load(); };
  const mute = async (id) => { await api.post(`/admin/users/${id}/mute`, { minutes: 1440 }); toast.success("User muted 24h"); load(); };
  const resolve = async (id, removePost) => { await api.post(`/admin/reports/${id}/resolve?remove_post=${removePost}`); load(); };

  if (fetching) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-cyan-400 animate-spin" /></div>;

  return (
    <div data-testid="admin-reports-list">
      <h2 className="font-heading font-bold text-xl text-white">Reported posts <span className="text-cyan-400">({reports.length})</span></h2>
      <p className="text-sm text-slate-400 mt-1">Reported posts are locked (author can't edit or delete). Review the content and take action.</p>
      <div className="mt-6 space-y-4">
        {reports.length === 0 ? (
          <p className="text-sm text-slate-500">No open reports. 🎉</p>
        ) : (
          reports.map((r) => (
            <div key={r.id} data-testid={`report-${r.id}`} className="electric-card rounded-2xl bg-black/50 backdrop-blur-xl border border-amber-500/20 p-5">
              <div className="flex items-center gap-3">
                <Avatar path={r.author?.avatar_path} name={r.author?.nickname} size="sm" />
                <div>
                  <p className="font-heading font-bold text-white text-sm">{r.author?.nickname}</p>
                  <p className="text-xs text-slate-500">reported by {r.reporter_nickname}{r.author_banned && " · already banned"}{r.author_muted && " · muted"}</p>
                </div>
              </div>
              {/* exact reported content shown inline */}
              <div className="mt-4 rounded-xl border border-white/10 bg-black/60 p-4" data-testid={`report-content-${r.id}`}>
                <p className="text-xs font-mono uppercase tracking-wider text-amber-400 mb-2">Reported content</p>
                <p className="text-sm text-slate-200 whitespace-pre-wrap break-words">{r.content_snapshot}</p>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button data-testid={`report-ban-${r.id}`} onClick={() => ban(r.author.id)} className="inline-flex items-center gap-1.5 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 text-xs font-semibold px-3 py-2 hover:bg-red-500/25"><Ban className="w-3.5 h-3.5" /> Ban user</button>
                <button data-testid={`report-mute-${r.id}`} onClick={() => mute(r.author.id)} className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-semibold px-3 py-2 hover:bg-amber-500/25"><VolumeX className="w-3.5 h-3.5" /> Mute 24h</button>
                <button data-testid={`report-removepost-${r.id}`} onClick={() => resolve(r.id, true)} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 border border-white/15 text-slate-300 text-xs font-semibold px-3 py-2 hover:bg-white/10"><Trash2 className="w-3.5 h-3.5" /> Remove post & resolve</button>
                <button data-testid={`report-dismiss-${r.id}`} onClick={() => resolve(r.id, false)} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 border border-white/15 text-slate-300 text-xs font-semibold px-3 py-2 hover:bg-white/10">Dismiss</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Users
function UsersAdmin({ adminId }) {
  const [users, setUsers] = useState([]);
  const [fetching, setFetching] = useState(true);
  const load = useCallback(() => { api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}).finally(() => setFetching(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => { await fn(); load(); };

  if (fetching) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-cyan-400 animate-spin" /></div>;

  return (
    <div data-testid="admin-users-list">
      <h2 className="font-heading font-bold text-xl text-white">Members <span className="text-cyan-400">({users.length})</span></h2>
      <div className="mt-6 space-y-2">
        {users.map((u) => (
          <div key={u.id} data-testid={`admin-user-${u.id}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/40 p-3 flex-wrap">
            <Avatar path={u.avatar_path} name={u.nickname || u.name} size="sm" />
            <div className="flex-1 min-w-[160px]">
              <p className="text-sm font-semibold text-white">{u.nickname || u.name}</p>
              <p className="text-xs text-slate-500">{u.email} · {u.role}{u.banned && " · banned"}{u.muted && " · muted"}</p>
              <p data-testid={`user-safety-ack-${u.id}`} className={`text-[11px] mt-0.5 ${u.safety_ack ? "text-emerald-400" : "text-amber-400"}`}>
                {u.safety_ack
                  ? `✓ Accepted security warning · ${new Date(u.safety_ack.at).toLocaleString()}`
                  : "⚠ Security warning not yet accepted"}
              </p>
            </div>
            {u.role !== "admin" ? (
              <div className="flex gap-2 flex-wrap">
                <button data-testid={`user-promote-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/promote`))} className="inline-flex items-center gap-1.5 text-xs text-cyan-300 border border-cyan-500/40 rounded-lg px-3 py-2 hover:bg-cyan-500/10"><Shield className="w-3.5 h-3.5" /> Make admin</button>
                {u.banned ? (
                  <button data-testid={`user-unban-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/unban`))} className="text-xs text-cyan-300 border border-cyan-500/40 rounded-lg px-3 py-2">Unban</button>
                ) : (
                  <button data-testid={`user-ban-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/ban`))} className="inline-flex items-center gap-1.5 text-xs text-red-300 border border-red-500/40 rounded-lg px-3 py-2 hover:bg-red-500/10"><Ban className="w-3.5 h-3.5" /> Ban</button>
                )}
                {u.muted ? (
                  <button data-testid={`user-unmute-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/unmute`))} className="inline-flex items-center gap-1.5 text-xs text-slate-300 border border-white/15 rounded-lg px-3 py-2"><Volume2 className="w-3.5 h-3.5" /> Unmute</button>
                ) : (
                  <button data-testid={`user-mute-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/mute`, { minutes: 1440 }))} className="inline-flex items-center gap-1.5 text-xs text-amber-300 border border-amber-500/40 rounded-lg px-3 py-2 hover:bg-amber-500/10"><VolumeX className="w-3.5 h-3.5" /> Mute</button>
                )}
              </div>
            ) : (
              u.id !== adminId && (
                <button data-testid={`user-demote-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/demote`))} className="inline-flex items-center gap-1.5 text-xs text-slate-300 border border-white/15 rounded-lg px-3 py-2 hover:border-red-400/50 hover:text-red-400">Remove admin</button>
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
